/**
 * Copyright (c) 2025 Adaptive Chaos Labs, LLC
 * All rights reserved.
 */

/**
 * **An artifact list's filter lives in the URL** (`LP12`, `docs/LISTS_AND_PANELS_DESIGN.md`).
 *
 * Written for Demand Sources (`LP-c`) and lifted for Request Strategies (slice 3b), since what a list
 * filter *is* — search, state, owner, sort, page, and a type's own filters — is a property of artifact
 * lists, not of one type. A list **declares** its parameters; this module reads them from the query
 * string, writes them back, and answers whether anything narrows the list. Nothing else keeps a copy:
 * a viewer convenience needs no storage of its own (`#1380`'s ruling on remembered state).
 */

import { isSystemManaged } from '@artifact-stewardship';

/**
 * **One filter parameter**: its query-string name, its value when absent, and how it is read and
 * written. `narrows` says whether a set value narrows the list — the sort and the page do not, which is
 * what separates *nothing matches* from *nothing exists*.
 */
export interface ListFilterParam<V> {
  key: string;
  empty: V;
  /** An unrecognised value reads as `empty` rather than being trusted: a pasted link is input like any other. */
  read: (raw: string | null) => V;
  /** `null` removes the parameter, so the default state is a clean URL. */
  write: (value: V) => string | null;
  narrows: boolean;
}

export type ListFilterDeclaration<F> = { readonly [K in keyof F]: ListFilterParam<F[K]> };

/** What every artifact list filters by. A type adds its own parameters beside these. */
export interface ArtifactListFilterBase {
  search: string;
  /** `true` enabled, `false` disabled, `null` either. */
  enabled: boolean | null;
  /**
   * **Whose** (Todd, 2026-09-28): `''` any, `'mine'` the viewer's, `'system'` system-managed, or an
   * owner's id. `'mine'` is a word rather than the viewer's id so a link to *my* items means the reader's.
   */
  owner: string;
  sortDir: 'asc' | 'desc';
  page: number;
}

const text = (key: string): ListFilterParam<string> => ({
  key,
  empty: '',
  read: (raw) => raw ?? '',
  write: (value) => value || null,
  narrows: true,
});

/** A choice from a closed set; anything else reads as `''`. */
export function oneOfParam<V extends string>(key: string, values: readonly V[]): ListFilterParam<V | ''> {
  return {
    key,
    empty: '',
    read: (raw) => ((values as readonly string[]).includes(raw ?? '') ? (raw as V) : ''),
    write: (value) => value || null,
    narrows: true,
  };
}

export const BASE_LIST_FILTER: ListFilterDeclaration<ArtifactListFilterBase> = {
  search: text('q'),
  enabled: {
    key: 'status',
    empty: null,
    read: (raw) => (raw === 'enabled' ? true : raw === 'disabled' ? false : null),
    write: (value) => (value === true ? 'enabled' : value === false ? 'disabled' : null),
    narrows: true,
  },
  owner: text('owner'),
  sortDir: {
    key: 'dir',
    empty: 'asc',
    read: (raw) => (raw === 'desc' ? 'desc' : 'asc'),
    write: (value) => (value === 'desc' ? 'desc' : null),
    narrows: false,
  },
  page: {
    key: 'page',
    empty: 1,
    read: (raw) => {
      const page = Number(raw);
      return Number.isInteger(page) && page > 1 ? page : 1;
    },
    write: (value) => (value > 1 ? String(value) : null),
    narrows: false,
  },
};

function entries<F>(decl: ListFilterDeclaration<F>): [keyof F, ListFilterParam<F[keyof F]>][] {
  return Object.entries(decl) as [keyof F, ListFilterParam<F[keyof F]>][];
}

/** The filter the URL states. */
export function listFilterFromSearchParams<F>(decl: ListFilterDeclaration<F>, params: URLSearchParams): F {
  const out = {} as F;
  for (const [name, param] of entries(decl)) out[name] = param.read(params.get(param.key));
  return out;
}

/**
 * The same URL with this change applied. A change to anything but the page returns to page one.
 * Every parameter that is not a filter is left as it was.
 */
export function searchParamsWithListFilter<F extends { page: number }>(
  decl: ListFilterDeclaration<F>,
  params: URLSearchParams,
  patch: Partial<F>,
): URLSearchParams {
  const next = new URLSearchParams(params);
  const merged: F = { ...listFilterFromSearchParams(decl, params), ...patch, page: patch.page ?? 1 };
  for (const [name, param] of entries(decl)) {
    const value = param.write(merged[name]);
    if (value) next.set(param.key, value);
    else next.delete(param.key);
  }
  return next;
}

/** Whether anything narrows the list — what separates *nothing matches* from *nothing exists*. */
export function isListFiltered<F>(decl: ListFilterDeclaration<F>, filter: F): boolean {
  return entries(decl).some(([name, param]) => param.narrows && filter[name] !== param.empty);
}

/** The URL with every filter cleared, keeping anything that is not a filter. */
export function searchParamsWithoutListFilter<F>(
  decl: ListFilterDeclaration<F>,
  params: URLSearchParams,
): URLSearchParams {
  const next = new URLSearchParams(params);
  for (const [, param] of entries(decl)) next.delete(param.key);
  return next;
}

/**
 * **Whether an artifact passes the Owner filter** (Todd, 2026-09-28). *System* is read through
 * `isSystemManaged`, the same answer the row's ownership pill gives, so the filter and the pill cannot
 * disagree. *Mine* needs the viewer; with none known it matches nothing rather than everything, since
 * *mine* is a narrowing.
 */
export function matchesOwnerFilter(
  artifact: { ownerId?: string; managedBy?: string },
  owner: string | undefined,
  viewerId: string | null,
): boolean {
  if (!owner) return true;
  if (owner === 'system') return isSystemManaged({ managedBy: artifact.managedBy, ownerId: artifact.ownerId });
  if (owner === 'mine') return viewerId !== null && artifact.ownerId === viewerId;
  return artifact.ownerId === owner;
}

/** Every named owner in the list, once, by name — the Owner filter's choices after Any, Mine and System. */
export function ownersOf(artifacts: readonly { ownerId?: string; ownerName?: string; managedBy?: string }[]): {
  id: string;
  name: string;
}[] {
  const byId = new Map<string, string>();
  for (const a of artifacts) {
    if (!a.ownerId || isSystemManaged({ managedBy: a.managedBy, ownerId: a.ownerId })) continue;
    if (!byId.has(a.ownerId)) byId.set(a.ownerId, a.ownerName || a.ownerId);
  }
  return [...byId].map(([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
}
